import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

export interface DashboardResponseDTO {
  kpis: DashboardKpisDTO;
  charts: DashboardChartsDTO;
  recentData: DashboardRecentDataDTO;
  projects: DashboardProjectsDTO;
  generatedAt: Date;
  /** Nivel de acceso: 'BASIC' | 'FULL' */
  accessLevel: 'BASIC' | 'FULL';
}

export interface DashboardKpisDTO {
  totalIncome: number;
  totalExpenses: number;
  activeParticipants: number;
  companies: number;
  netProfit: number;
  pendingWorkOrders: number;
  pendingPayments: number;
  totalTransactions: number;
}

export interface DashboardChartsDTO {
  monthlyIncomeVsExpenses: MonthlyFinancialDataDTO[];
  paymentStatus: DashboardPaymentStatusDTO;
  expensesByCategory: CategorySpendingDTO[];
  monthlyTrends: MonthlyTrendDTO[];
}

export interface DashboardRecentDataDTO {
  recentParticipants: DashboardRecentItemDTO[];
  recentCompanies: DashboardRecentItemDTO[];
  recentThirdParties: DashboardRecentItemDTO[];
  pendingWorkOrders: DashboardRecentItemDTO[];
  recentExpenseVouchers: DashboardRecentItemDTO[];
  recentIncomeReceipts: DashboardRecentItemDTO[];
}

export interface DashboardProjectsDTO {
  topProfitableProjects: TopProjectDTO[];
  activeProjects: ProjectSummaryDTO[];
  totalActiveProjects: number;
  completedProjectsThisMonth: number;
}

export interface MonthlyFinancialDataDTO {
  month: string;
  monthNumber: number;
  income: number;
  expenses: number;
  netIncome: number;
}

export interface DashboardPaymentStatusDTO {
  paid: number;
  pending: number;
  overdue: number;
  paidAmount: number;
  pendingAmount: number;
  overdueAmount: number;
}

export interface CategorySpendingDTO {
  categoryName: string;
  amount: number;
  color: string;
  percentage: number;
}

export interface MonthlyTrendDTO {
  month: string;
  monthNumber: number;
  newParticipants: number;
  newCompanies: number;
  completedWorkOrders: number;
  revenue: number;
}

export interface DashboardRecentItemDTO {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  date: Date;
  amount?: number;
  status: string;
  statusColor: string;
  icon: string;
  type: string;
}

export interface TopProjectDTO {
  id: number;
  name: string;
  profit: number;
  revenue: number;
  expenses: number;
  profitMargin: number;
  totalEnrollments: number;
  activeEnrollments: number;
}

export interface ProjectSummaryDTO {
  id: number;
  name: string;
  status: string;
  startDate: Date;
  endDate?: Date;
  budget: number;
  spentAmount: number;
  progress: number;
  enrolledParticipants: number;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  private _baseUrl: string = "";
  private _controllerName: string = "";

  constructor(private _http: HttpClient) {
    this._baseUrl = environment.apiUrl;
    this._controllerName = "Dashboard"
  }

  getDashboardData(year?: number, month?: number): Observable<DashboardResponseDTO> {
    const queryParams: string[] = [];
    if (year) queryParams.push(`year=${year}`);
    if (month) queryParams.push(`month=${month}`);
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const url = `${this._baseUrl}/${this._controllerName}${queryString}`;

    return this._http.get<DashboardResponseDTO>(url)
      .pipe(catchError(this.handleErrors.bind(this)));
  }

  private handleErrors(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.log("Ocurrió un error", error.error.message);
    } else {
      console.log(`Ocurrió un error ${error.status}, ${error.error}`);
    }
    return throwError(() => new Error('Algo salió mal; por favor, intente nuevamente más tarde.'));
  }
}
